import type { DictateStep } from '../step-registry';

/**
 * Cumulative analytics state for the dictate onboarding funnel, kept in
 * `localStorage` so it survives the provider remount caused by the sign-in auth
 * navigation (external-browser deep-link on the Auth0 desktop path, in-webview
 * navigation on the Kinde path, or a quit-during-auth + relaunch).
 *
 * In-memory refs reset on every remount, so without this the `onboarding_completed`
 * metrics would only describe the post-auth session: `total_duration_ms` would
 * measure a fraction of the funnel and `steps_completed` would miss every pre-auth
 * step. Versioned + minimal (timestamp + step ids only — no PHI), per the
 * localStorage authoring guidance.
 */
const STORAGE_KEY = 'heidi:dictate-onboarding-analytics:v1';

export type OnboardingAnalyticsSession = {
  /** Unix epoch ms when the funnel started (set once on `onboarding_started`). */
  flowStartTimeMs: number;
  /** Unique step ids completed so far across the whole funnel. */
  completedSteps: DictateStep[];
  /** Whether `onboarding_started` has already fired for this funnel. */
  onboardingStartedTracked: boolean;
  /** Whether the entry `screen_viewed` (dictate onboarding) has already fired. */
  entryScreenViewedTracked: boolean;
};

/** Read the persisted funnel session, or `null` when absent/unreadable. */
export function loadAnalyticsSession(): OnboardingAnalyticsSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<OnboardingAnalyticsSession>;
    if (typeof parsed.flowStartTimeMs !== 'number') return null;

    return {
      flowStartTimeMs: parsed.flowStartTimeMs,
      completedSteps: Array.isArray(parsed.completedSteps) ? parsed.completedSteps : [],
      onboardingStartedTracked: Boolean(parsed.onboardingStartedTracked),
      // Older sessions only tracked `onboarding_started`; the entry screen was
      // always fired in the same tick, so treat a started funnel as already viewed.
      entryScreenViewedTracked: Boolean(
        parsed.entryScreenViewedTracked ?? parsed.onboardingStartedTracked
      ),
    };
  } catch {
    // Throws in private mode / when storage is disabled; analytics is best-effort.
    return null;
  }
}

/** Persist the funnel session (best-effort — swallows storage failures). */
export function saveAnalyticsSession(session: OnboardingAnalyticsSession): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    // Best-effort: ignore quota / private-mode failures.
  }
}

/** Remove the funnel session once the flow completes or is exited. */
export function clearAnalyticsSession(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Best-effort.
  }
}
